// Footnotes. A definition is a container of blocks (several paragraphs and
// lists), and definitions are gathered at the end of the document: on parse by
// `collectFootnoteDefs` (JSON), while editing by `FootnoteBlocks` (append
// transaction). The inline reference `[^label]` is an atom.
//
// Exception: one empty paragraph after the definitions stays where it is. It is
// the editing scaffold StarterKit's `trailingNode` adds; without the exception
// the two append transactions undo each other and never settle. Both the JSON
// step and the plugin have it - when only one did, every save and reload added
// one more empty paragraph (an `&nbsp;` line in the markdown). The trailing
// empty paragraph itself is never written (`Doc.renderMarkdown`).
//
// Invariant: definitions live directly under the doc. The schema allows them
// anywhere a block goes (`> [^a]: inside` parses), so `collectFootnoteDefs`
// lifts them to the top level, and a parent left with no children (a quote,
// another definition) is removed - a node breaking `block+` could break
// ProseMirror checks, node views and the Y binding. Parents of parents left
// empty go too; only the doc itself keeps one empty paragraph if it would be
// empty. The live plugin does not lift (removing from a parent and appending is
// costly on Yjs; a save and reload settles it).
//
// Order: definitions follow the order of the first reference to each label in
// the body (everything except definitions, including table cells and nested
// lists). Definitions nobody references keep their relative order at the end.
// References inside definitions do not count (definitions referencing each
// other could make a cycle). The JSON step and the plugin share the order code.
//
// Concurrent editing: reordering is delete + insert, not a move, so two
// clients seeing the same unsorted end at once can both insert their copy and
// duplicate definitions after the merge (the same reason list splitting skips
// y-sync transactions). Like the other plugins, y-sync transactions are not reordered.

import {
  Extension,
  type JSONContent,
  type MarkdownParseHelpers,
  type MarkdownRendererHelpers,
  type MarkdownToken,
  Node,
} from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import {
  Plugin,
  PluginKey,
  type Selection,
  TextSelection,
  type Transaction,
} from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { blockIdDomAttrs } from "./block-id";
import { isYSyncTransaction } from "./block-id-plugin";
import { Lines } from "./lines";
import { renderBlockSiblings } from "./render-blocks";

const DEF_START_RE = /^\[\^([^\]\s]+)\]:[ \t]?(.*)$/;

/**
 * The extent of a definition starting with `[^label]:`: following lines
 * indented by 4 spaces (or a tab), and blank lines followed by indented lines.
 * Returns the label, the body (indent removed, trailing newline) and the
 * characters consumed.
 */
export function scanFootnoteDef(
  src: string,
): { label: string; body: string; rawLength: number } | undefined {
  const lines = new Lines(src);
  const match = DEF_START_RE.exec(lines.at(0) ?? "");
  if (!match) return undefined;
  const body: string[] = [match[2]];
  let end = 1;
  let i = 1;
  for (let line = lines.at(i); line !== undefined; line = lines.at(i)) {
    if (/^(?: {4}|\t)/.test(line)) {
      body.push(line.replace(/^(?: {4}|\t)/, ""));
      end = i + 1;
      i += 1;
      continue;
    }
    if (line === "") {
      let j = i;
      while (lines.at(j) === "") j += 1;
      const next = lines.at(j);
      if (next !== undefined && /^(?: {4}|\t)/.test(next)) {
        for (let k = i; k < j; k++) body.push("");
        i = j;
        continue;
      }
    }
    break;
  }
  const consumed = lines.join(0, end);
  return {
    label: match[1],
    body: `${body.join("\n")}\n`,
    rawLength: consumed.length + (src.length > consumed.length ? 1 : 0),
  };
}

export const FootnoteDef = Node.create({
  name: "footnoteDef",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return { label: { default: "" } };
  },

  parseHTML() {
    return [{ tag: "div[data-footnote-def]" }];
  },

  renderHTML({ node }) {
    return [
      "div",
      { "data-footnote-def": node.attrs.label, ...blockIdDomAttrs(node) },
      [
        "span",
        { class: "trameria-footnote-label", contenteditable: "false" },
        `[^${node.attrs.label}]`,
      ],
      ["div", { class: "trameria-footnote-body" }, 0],
    ];
  },

  markdownTokenName: "footnoteDef",
  markdownTokenizer: {
    name: "footnoteDef",
    level: "block" as const,
    start(src: string) {
      const match = src.match(/^\[\^([^\]\s]+)\]:/m);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string, _tokens: MarkdownToken[], lexer) {
      const scan = scanFootnoteDef(src);
      if (!scan) return undefined;
      // As in details.ts: block-level `text` from recursive lexing becomes
      // `paragraph` (no raw text directly under `block+`).
      const tokens = lexer
        .blockTokens(scan.body)
        .map((t) =>
          t.type === "text"
            ? ({ ...t, type: "paragraph" } as MarkdownToken)
            : t,
        );
      return {
        type: "footnoteDef",
        raw: src.slice(0, scan.rawLength),
        label: scan.label,
        tokens,
      };
    },
  },

  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    const parseBlockChildren = h.parseBlockChildren ?? h.parseChildren;
    let content = parseBlockChildren(token.tokens ?? []);
    if (content.length === 0) content = [{ type: "paragraph" }];
    return h.createNode("footnoteDef", { label: token.label }, content);
  },

  renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
    // Blocks are joined like the doc (blank lines between them), then every
    // line after the first is indented.
    const body = renderBlockSiblings(node.content ?? [], h).replace(
      /^\n+|\n+$/g,
      "",
    );
    const label = node.attrs?.label;
    const [first, ...rest] = body.split("\n");
    const indented = rest.map((line) => (line === "" ? "" : `    ${line}`));
    // An empty body writes `[^label]:` with no trailing space; with one, tools
    // that strip trailing spaces would make every normalization add it back.
    const head = first === "" ? `[^${label}]:` : `[^${label}]: ${first}`;
    return [head, ...indented].join("\n");
  },
});

/** The inline reference `[^label]`. */
export const FootnoteRef = Node.create({
  name: "footnoteRef",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return { label: { default: "" } };
  },

  parseHTML() {
    return [{ tag: "sup[data-footnote-ref]" }];
  },

  // The class is the one the screen's node view uses, so the static view and
  // the live editor share one style rule.
  renderHTML({ node }) {
    return [
      "sup",
      {
        class: "trameria-footnote-ref",
        "data-footnote-ref": "true",
        "data-label": node.attrs?.label,
      },
      `[^${node.attrs?.label}]`,
    ];
  },

  markdownTokenName: "footnoteRef",
  markdownTokenizer: {
    name: "footnoteRef",
    level: "inline",
    start(src: string) {
      const match = src.match(/\[\^([^\]\s]+)\]/);
      return match ? (match.index ?? -1) : -1;
    },
    tokenize(src: string) {
      const match = /^\[\^([^\]\s]+)\]/.exec(src);
      if (!match) return undefined;
      return { type: "footnoteRef", raw: match[0], label: match[1] };
    },
  },
  parseMarkdown(token: MarkdownToken, h: MarkdownParseHelpers) {
    return h.createNode("footnoteRef", { label: token.label }, []);
  },
  renderMarkdown(node: JSONContent) {
    return `[^${node.attrs?.label}]`;
  },
});

// --- order (shared by the JSON step and the plugin) ----------------------------

/**
 * Labels of references in the body (outside every definition), in order of
 * first appearance: label -> 1-based position.
 */
function bodyLabelOrderFromJSON(root: JSONContent): Map<string, number> {
  const order = new Map<string, number>();
  const visit = (node: JSONContent) => {
    for (const child of node.content ?? []) {
      if (child.type === "footnoteRef") {
        const label = String(child.attrs?.label ?? "");
        if (!order.has(label)) order.set(label, order.size + 1);
        continue;
      }
      if (child.type === "footnoteDef") continue;
      visit(child);
    }
  };
  visit(root);
  return order;
}

/** {@link bodyLabelOrderFromJSON} for a PM node. */
function bodyLabelOrderFromPM(doc: PMNode): Map<string, number> {
  const order = new Map<string, number>();
  const visit = (node: PMNode) => {
    node.forEach((child) => {
      if (child.type.name === "footnoteRef") {
        const label = String(child.attrs.label);
        if (!order.has(label)) order.set(label, order.size + 1);
        return;
      }
      if (child.type.name === "footnoteDef") return;
      visit(child);
    });
  };
  visit(doc);
  return order;
}

/** Sorts definitions by `order`; unreferenced ones keep their relative order at the end. */
function sortDefsByOrder<T>(
  defs: readonly T[],
  labelOf: (def: T) => string,
  order: Map<string, number>,
): T[] {
  return defs
    .map((def, index) => {
      const rank = order.get(labelOf(def));
      return { def, rank: rank !== undefined ? rank : 1e9 + index };
    })
    .sort((a, b) => a.rank - b.rank)
    .map((x) => x.def);
}

function labelOfJSON(def: JSONContent): string {
  return String(def.attrs?.label ?? "");
}

function labelOfPM(def: PMNode): string {
  return String(def.attrs.label);
}

// --- lifting nested definitions (JSON step only) --------------------------------

/**
 * Parents that are not removed when emptied: table cells. Removing a cell
 * leaves a row short of columns and changes the table's shape in GFM; a cell is
 * `paragraph+`, so one empty paragraph is the right empty cell.
 */
const KEEP_EMPTY_TYPES = new Set(["tableCell", "tableHeader"]);

/**
 * Removes definitions from the children of `node`, recursively (definitions
 * inside definitions too), collecting them in document order. Returns the same
 * node when nothing was found, and `null` when the node itself was left with no
 * children (the caller removes it; except table cells, which keep one empty
 * paragraph).
 */
function liftNestedFootnoteDefs(node: JSONContent): {
  node: JSONContent | null;
  extracted: JSONContent[];
} {
  const content = node.content;
  if (!content || content.length === 0) return { node, extracted: [] };
  const kept: JSONContent[] = [];
  const extracted: JSONContent[] = [];
  let changed = false;
  for (const child of content) {
    if (child.type === "footnoteDef") {
      changed = true;
      const inner = liftNestedFootnoteDefs(child);
      // A definition that held only nested definitions is gone after lifting them.
      if (inner.node !== null) extracted.push(inner.node);
      extracted.push(...inner.extracted);
      continue;
    }
    const result = liftNestedFootnoteDefs(child);
    if (result.node !== child || result.extracted.length > 0) changed = true;
    if (result.node !== null) kept.push(result.node);
    extracted.push(...result.extracted);
  }
  if (!changed) return { node, extracted: [] };
  if (kept.length === 0) {
    if (typeof node.type === "string" && KEEP_EMPTY_TYPES.has(node.type)) {
      return { node: { ...node, content: [{ type: "paragraph" }] }, extracted };
    }
    return { node: null, extracted };
  }
  return { node: { ...node, content: kept }, extracted };
}

/**
 * Gathers the definitions at the end of the doc, in reference order, after
 * lifting nested ones to the top level (parents left empty are removed). One
 * trailing empty paragraph stays after them. Returns the same instance when
 * nothing changes.
 */
export function collectFootnoteDefs(doc: JSONContent): JSONContent {
  const blocks = doc.content ?? [];
  const trailingBlank =
    blocks.length > 0 && isBlankParagraphJSON(blocks[blocks.length - 1]);
  const others: JSONContent[] = [];
  const topDefs: JSONContent[] = [];
  const nestedDefs: JSONContent[] = [];
  let lifted = false;

  for (const block of blocks) {
    const { node, extracted } = liftNestedFootnoteDefs(block);
    if (node !== block) lifted = true;
    if (node !== null) {
      (block.type === "footnoteDef" ? topDefs : others).push(node);
    }
    nestedDefs.push(...extracted);
  }

  const allDefs = [...topDefs, ...nestedDefs];
  if (allDefs.length === 0) return doc;

  // Counted on the whole doc (definitions are skipped at any depth, so lifting
  // does not change the result).
  const order = bodyLabelOrderFromJSON(doc);
  const sorted = sortDefsByOrder(allDefs, labelOfJSON, order);

  // The trailing empty paragraph (the last of `others`) stays after the definitions.
  const head = trailingBlank ? others.slice(0, -1) : others;
  const tail = trailingBlank ? others.slice(-1) : [];
  const content = [...head, ...sorted, ...tail];

  const same =
    !lifted &&
    content.length === blocks.length &&
    content.every((b, i) => b === blocks[i]);
  if (same) return doc;

  return { ...doc, content };
}

/** An empty paragraph (the same notion as `Doc.renderMarkdown` drops at the end). */
function isBlankParagraphJSON(node: JSONContent): boolean {
  return node.type === "paragraph" && (node.content?.length ?? 0) === 0;
}

/**
 * Numbers of the definitions under the doc: label -> 1-based position in the
 * list at the end (labels without a definition are absent). This is the
 * displayed number, not the reference order the sorting uses; an unreferenced
 * definition in between shifts the numbers.
 */
export function footnoteOrder(doc: PMNode): Map<string, number> {
  const order = new Map<string, number>();
  doc.forEach((child) => {
    if (child.type.name === "footnoteDef") {
      const label = String(child.attrs.label);
      if (!order.has(label)) order.set(label, order.size + 1);
    }
  });
  return order;
}

/** The definition of `label` under the doc, or null. */
export function findFootnoteDef(
  doc: PMNode,
  label: string,
): { pos: number; node: PMNode } | null {
  let hit: { pos: number; node: PMNode } | null = null;
  doc.forEach((child, offset) => {
    if (
      !hit &&
      child.type.name === "footnoteDef" &&
      child.attrs.label === label
    ) {
      hit = { pos: offset, node: child };
    }
  });
  return hit;
}

export const footnoteBlocksKey = new PluginKey<DecorationSet>(
  "trameriaFootnoteBlocks",
);

/**
 * The doc ends with an empty paragraph (StarterKit's `trailingNode` scaffold).
 * One after the definitions is accepted as sorted; otherwise trailingNode adds
 * a paragraph, the definitions move after it, it adds another... and typing
 * one character in a document with footnotes froze the tab.
 */
function isTrailingBlank(doc: PMNode): boolean {
  const last = doc.lastChild;
  return (
    last !== null && last.type.name === "paragraph" && last.content.size === 0
  );
}

/**
 * The plugin side of {@link collectFootnoteDefs} (top-level definitions only,
 * no lifting): the transaction moving definitions to the end in reference
 * order, or undefined when already so (the second round must return undefined).
 *
 * A caret inside a moved definition keeps its offset within it (empty
 * selections only).
 */
function collectFootnoteDefsTr(
  tr: Transaction,
  doc: PMNode,
  selection: Selection,
): Transaction | undefined {
  // A cheap top-level pass first; documents without footnotes stop here.
  const defs: { pos: number; node: PMNode }[] = [];
  const typesInOrder: string[] = [];
  let pos = 0;
  doc.forEach((child) => {
    typesInOrder.push(child.type.name);
    if (child.type.name === "footnoteDef") defs.push({ pos, node: child });
    pos += child.nodeSize;
  });
  if (defs.length === 0) return undefined;

  const order = bodyLabelOrderFromPM(doc);
  const sorted = sortDefsByOrder(
    defs.map((d) => d.node),
    labelOfPM,
    order,
  );

  const firstDefIndex = typesInOrder.indexOf("footnoteDef");
  const tail = typesInOrder.slice(firstDefIndex);
  const keepsBlank = tail.length >= 2 && isTrailingBlank(doc);
  const isAtTail = (keepsBlank ? tail.slice(0, -1) : tail).every(
    (t) => t === "footnoteDef",
  );
  const alreadySorted = defs.every((d, i) => d.node === sorted[i]);
  if (isAtTail && alreadySorted) return undefined;

  const movedFrom = selection.empty
    ? defs.find(
        (d) =>
          selection.from >= d.pos && selection.from <= d.pos + d.node.nodeSize,
      )
    : undefined;
  const relOffset = movedFrom ? selection.from - movedFrom.pos : null;

  // Delete from the back so positions stay valid.
  for (let i = defs.length - 1; i >= 0; i--) {
    const { pos: defPos, node } = defs[i];
    tr.delete(defPos, defPos + node.nodeSize);
  }
  // Insert before a trailing empty paragraph; at the very end, trailingNode
  // would add another and the loop would not end.
  const lastAfter = tr.doc.lastChild;
  const tailStart =
    lastAfter !== null && isTrailingBlank(tr.doc)
      ? tr.doc.content.size - lastAfter.nodeSize
      : tr.doc.content.size;
  tr.insert(tailStart, sorted);

  if (movedFrom && relOffset !== null) {
    const newIndex = sorted.indexOf(movedFrom.node);
    if (newIndex !== -1) {
      let newDefPos = tailStart;
      for (let i = 0; i < newIndex; i++) newDefPos += sorted[i].nodeSize;
      const $pos = tr.doc.resolve(newDefPos + relOffset);
      tr.setSelection(TextSelection.near($pos));
    }
  }
  return tr;
}

/**
 * The decoration spec key carrying the number. Node decoration `attrs` land on
 * the node view's outer DOM, which a React node view cannot read; the spec
 * reaches `NodeViewProps.decorations` ({@link footnoteNumberOf}). The attrs
 * stay for CSS.
 */
export const FOOTNOTE_NUMBER_SPEC = "trameriaFootnoteNumber";

/**
 * The decoration spec key saying whether a definition is referenced from the
 * body. The definition's mark jumps to its reference; with none there is
 * nowhere to jump ({@link footnoteReferencedOf}).
 */
export const FOOTNOTE_REFERENCED_SPEC = "trameriaFootnoteReferenced";

/** The footnote number from a node view's decorations (null: no definition). */
export function footnoteNumberOf(
  decorations: readonly { spec?: unknown }[],
): number | null {
  for (const deco of decorations) {
    const spec = deco.spec as Record<string, unknown> | null | undefined;
    const n = spec?.[FOOTNOTE_NUMBER_SPEC];
    if (typeof n === "number") return n;
  }
  return null;
}

/** Whether the body references this definition (false without decorations). */
export function footnoteReferencedOf(
  decorations: readonly { spec?: unknown }[],
): boolean {
  for (const deco of decorations) {
    const spec = deco.spec as Record<string, unknown> | null | undefined;
    const referenced = spec?.[FOOTNOTE_REFERENCED_SPEC];
    if (typeof referenced === "boolean") return referenced;
  }
  return false;
}

/**
 * The decorations: references get their number (or `data-missing`), and
 * definitions get their number and whether they are referenced. Computed even
 * without definitions (references still need `data-missing`).
 */
function computeFootnoteDecorations(doc: PMNode): DecorationSet {
  const order = footnoteOrder(doc);
  const decos: Decoration[] = [];
  const referenced = new Set<string>();
  doc.descendants((n, nodePos) => {
    if (n.type.name !== "footnoteRef") return;
    const label = String(n.attrs.label);
    referenced.add(label);
    const number = order.get(label);
    const attrs: Record<string, string> =
      number === undefined
        ? { "data-missing": "true" }
        : { "data-n": String(number) };
    decos.push(
      Decoration.node(nodePos, nodePos + n.nodeSize, attrs, {
        [FOOTNOTE_NUMBER_SPEC]: number ?? null,
      }),
    );
  });
  doc.forEach((child, offset) => {
    if (child.type.name !== "footnoteDef") return;
    const label = String(child.attrs.label);
    const n = order.get(label);
    if (n === undefined) return;
    const hasRef = referenced.has(label);
    const attrs: Record<string, string> = { "data-n": String(n) };
    if (!hasRef) attrs["data-unreferenced"] = "true";
    decos.push(
      Decoration.node(offset, offset + child.nodeSize, attrs, {
        [FOOTNOTE_NUMBER_SPEC]: n,
        [FOOTNOTE_REFERENCED_SPEC]: hasRef,
      }),
    );
  });
  return DecorationSet.create(doc, decos);
}

/**
 * Reordering and decorations. Decorations live in the plugin state; a
 * transaction that does not change the document only maps them.
 */
export function createFootnoteBlocksPlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: footnoteBlocksKey,

    state: {
      init: (_config, state) => computeFootnoteDecorations(state.doc),
      apply(tr, decos, _oldState, newState) {
        if (!tr.docChanged) return decos.map(tr.mapping, tr.doc);
        return computeFootnoteDecorations(newState.doc);
      },
    },

    appendTransaction(transactions, _old, newState) {
      if (!transactions.some((tr) => tr.docChanged)) return undefined;
      if (transactions.some(isYSyncTransaction)) return undefined;
      return collectFootnoteDefsTr(
        newState.tr,
        newState.doc,
        newState.selection,
      );
    },

    props: {
      decorations(state) {
        return footnoteBlocksKey.getState(state) ?? null;
      },
    },
  });
}

/** {@link createFootnoteBlocksPlugin} for live editing only. */
export const FootnoteBlocks = Extension.create({
  name: "footnoteBlocks",

  addProseMirrorPlugins() {
    return [createFootnoteBlocksPlugin()];
  },
});
